/**
 * Internal Bulk Section App-Visibility Write
 *
 * POST /internal/sections/app-visibility/bulk  (checkApiKeyMiddleware —
 * internal, not a public `/v1/*` route)
 *
 * The dashboard writes section visibility ONE space at a time
 * (`PATCH /v1/spaces/:id/settings`). A school that wants only its homerooms in
 * the app has thousands of other current-year section rows to hide — one row
 * per section per quarter term — which is not a thing to click. This endpoint
 * is the bulk lever: it sets `Section.appVisibility` across a selector'd set of
 * sections at ONE school.
 *
 * It writes the 3-state enum ONLY. The wire `showInApp` every reader serves is
 * DERIVED from that enum at read time (`computeEffectiveVisibility` →
 * `deriveShowInAppWire`), so the legacy `Section.showInApp` column is
 * deliberately not touched. There is likewise nothing to invalidate: the spaces
 * read model computes visibility live from the column, and no Stream /
 * OneSignal / cache state mirrors it.
 *
 * `dry_run` defaults to true and performs ZERO writes, returning the same
 * counts and the same per-row plan a live run would produce.
 */

import { z } from 'zod'

/** Mirrors the Prisma `AppVisibility` enum: DEFAULT defers to the type-based auto-rule. */
export const InternalSectionAppVisibilityValueSchema = z.enum(['SHOWN', 'HIDDEN', 'DEFAULT'])
export type InternalSectionAppVisibilityValue = z.infer<typeof InternalSectionAppVisibilityValueSchema>

/**
 * The query selector. Always additionally scoped to `school_id` by the use
 * case — a filter can never reach another school's rows.
 *
 * Course-code prefixes are matched CASE-SENSITIVELY, in application code, not
 * by a SQL `LIKE`: the MySQL collation is case-insensitive, so `HR ` and `hr `
 * would be the same prefix to the database and are not the same course code.
 */
export const InternalBulkSectionAppVisibilityFilterSchema = z.object({
  offering_type: z
    .string()
    .min(1)
    .describe("Matched against Section.offeringType EXACTLY (e.g. 'ACADEMIC'), not normalized."),
  school_year: z
    .literal('current')
    .optional()
    .prefault('current')
    .describe("Only 'current' is supported; resolves via getCurrentSchoolYear and matches Section.schoolYearId."),
  course_code_prefixes: z
    .array(z.string().min(1))
    .min(1)
    .optional()
    .describe('Keep ONLY rows whose platformCourseCode starts with one of these (case-sensitive).'),
  exclude_course_code_prefixes: z
    .array(z.string().min(1))
    .min(1)
    .optional()
    .describe(
      'Drop rows whose platformCourseCode starts with one of these (case-sensitive). Applied after the keep list.'
    )
})
export type InternalBulkSectionAppVisibilityFilter = z.infer<typeof InternalBulkSectionAppVisibilityFilterSchema>

export const InternalBulkSectionAppVisibilityRequestSchema = z
  .object({
    school_id: z.string().min(1).describe('School public ID (CUID) or slug'),
    app_visibility: InternalSectionAppVisibilityValueSchema.describe('The value to write to every matched section'),
    section_ids: z
      .array(z.string().min(1))
      .min(1)
      .optional()
      .describe(
        'Section PUBLIC ids, all of which must belong to school_id. Mutually exclusive with filter; unknown or foreign ids fail the whole request.'
      ),
    filter: InternalBulkSectionAppVisibilityFilterSchema.optional().describe(
      "Query selector over the school's sections. Mutually exclusive with section_ids."
    ),
    dry_run: z
      .boolean()
      .optional()
      .prefault(true)
      .describe('Defaults to true. A dry run performs ZERO writes and returns the counts a live run would produce.')
  })
  .refine(input => Boolean(input.section_ids?.length) !== Boolean(input.filter), {
    message: 'Exactly one of section_ids or filter is required',
    path: ['section_ids']
  })
export type InternalBulkSectionAppVisibilityRequest = z.infer<typeof InternalBulkSectionAppVisibilityRequestSchema>

/**
 * One matched section. `before`/`after` are the stored enum, not the derived
 * wire boolean; on a dry run `after` is what the live run WOULD store.
 * `course_code`/`term` are the raw `platformCourseCode` / `platformDurationName`
 * and are frequently null — the aggregate maps key those rows as `UNKNOWN`.
 */
export const InternalBulkSectionAppVisibilityRowSchema = z.object({
  id: z.string().describe('Section public ID'),
  name: z.string(),
  course_code: z.string().nullable(),
  term: z.string().nullable(),
  before: InternalSectionAppVisibilityValueSchema,
  after: InternalSectionAppVisibilityValueSchema
})
export type InternalBulkSectionAppVisibilityRow = z.infer<typeof InternalBulkSectionAppVisibilityRowSchema>

export const InternalBulkSectionAppVisibilityResponseSchema = z.object({
  dry_run: z.boolean(),
  school: z.object({
    id: z.string().describe('School public ID'),
    slug: z.string(),
    name: z.string()
  }),
  app_visibility: InternalSectionAppVisibilityValueSchema,
  /** Rows the selector resolved to, after every filter. */
  matched: z.number().int(),
  /** Matched rows whose stored value DIFFERS from app_visibility — exactly the rows a live run writes. */
  changed: z.number().int(),
  /** Matched rows already holding app_visibility. `changed + unchanged === matched`. */
  unchanged: z.number().int(),
  /** Matched rows by their CURRENT stored value. All three enum keys are always present. */
  by_previous_visibility: z.record(InternalSectionAppVisibilityValueSchema, z.number().int()),
  /** Matched rows by raw platformCourseCode; null course codes are keyed `UNKNOWN`. */
  by_course_code: z.record(z.string(), z.number().int()),
  /** Matched rows by raw platformDurationName; null terms are keyed `UNKNOWN`. */
  by_term: z.record(z.string(), z.number().int()),
  /** EVERY matched row (changed and unchanged), ordered by name then term. */
  rows: z.array(InternalBulkSectionAppVisibilityRowSchema)
})
export type InternalBulkSectionAppVisibilityResponse = z.infer<typeof InternalBulkSectionAppVisibilityResponseSchema>
